/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import { saveAs } from 'file-saver'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  DownloadOutlined,
  EditOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { Avatar, Input, Spin, Table, Typography } from 'antd'
import Button from 'src/components/ts-components/Button'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import Text from 'src/components/ts-components/Text'
import Icon from '../Icon'
import { media } from '../Responsive'
import AddSuspensionSheet from './AddSuspensionSheet'

dayjs.extend(timezone)

const Suspensions = ({
  isProfileView = false,
  loading = true,
  suspensions = [],
  refresh,
  exportId,
  exportFileName,
  hasPermission,
  onlyDisplayActive = false,
  scheduleId = null,
}) => {
  const [activeFilter, setActiveFilter] = useState('all')
  const [playerFilter, setPlayerFilter] = useState('')

  const receivedSuspensions = useMemo(() => suspensions, [suspensions])

  const filteredSuspensions = useMemo(() => {
    return receivedSuspensions.filter((s) => {
      if (onlyDisplayActive) {
        return s.is_active
      }

      let shouldInclude = true
      if (activeFilter === 'active') {
        shouldInclude = s.is_active
      } else if (activeFilter === 'completed') {
        shouldInclude = !s.is_active
      }

      if (playerFilter) {
        const name = s?.player?.name_first
          ? `${s?.player?.name_first} ${s?.player?.name_last}`
          : '-'
        return (
          shouldInclude &&
          name.toLowerCase().includes(playerFilter.toLowerCase())
        )
      }

      return shouldInclude
    })
  }, [receivedSuspensions, onlyDisplayActive, activeFilter, playerFilter])

  const columns = useMemo(() => {
    const cols = []

    if (!isProfileView) {
      cols.push({
        title: 'Player',
        key: 'player',
        dataIndex: 'player',
        width: 270,
        render: (player) => {
          if (!player?.id) return '-'
          return (
            <Link
              to={`${ROUTES.PLAYER_ROOT}/${player.id}`}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                max-width: 270px;
              `}
            >
              <Avatar
                src={
                  getImageThumbnailId(player) ||
                  '/images/placeholders/hockey/player.png'
                }
                shape='square'
                icon={<Icon name='user' fontSize={14} />}
                css={css`
                  border: 1px solid #dbdbdb;
                  background-color: white;
                  margin: 0;
                `}
              />
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  min-width: 0;
                `}
              >
                <Typography.Text
                  strong
                  ellipsis
                  css={css`
                    color: ${colors.NEUTRAL_0};
                  `}
                >
                  {player?.name_first} {player?.name_last}
                </Typography.Text>
              </div>
            </Link>
          )
        },
      })
    }

    cols.push({
      title: 'Team',
      key: 'team',
      dataIndex: 'team',
      width: 270,
      render: (team) => (
        <Link
          to={`${ROUTES.TEAM_ROOT}/${team?.id}${
            scheduleId ? `?sn_schedule=${scheduleId}` : ''
          }`}
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            max-width: 270px;
          `}
        >
          <Avatar
            src={
              getImageThumbnailId(team) ||
              '/images/placeholders/hockey/team.png'
            }
            icon={<Icon name='user-friends' fontSize={14} />}
            shape='square'
            css={css`
              border: 1px solid #dbdbdb;
              background-color: white;
              padding: 2px;
            `}
          />
          <Typography.Text strong ellipsis>
            {team?.name_full}
          </Typography.Text>
        </Link>
      ),
    })

    if (isProfileView) {
      cols.push({
        title: 'Competition',
        key: 'schedule',
        dataIndex: 'schedule',
        render: (schedule) => (
          <Link to={`${ROUTES.SCHEDULE_ROOT}/${schedule?.id}`}>
            {schedule?.name_full}
          </Link>
        ),
      })
    }

    if (!onlyDisplayActive) {
      cols.push({
        title: 'Status',
        key: 'status',
        render: (_, record) => (record.is_active ? 'Active' : 'Completed'),
      })
    }

    cols.push({
      title: 'Date',
      key: 'start_date',
      dataIndex: 'start_date',
      render: (date) => (date ? dayjs.utc(date).format('YYYY-MM-DD') : '-'),
    })

    cols.push({
      title: 'Return Date',
      key: 'end_date',
      render: (_, record) => {
        const { end_date, enforcement } = record
        return end_date && enforcement?.id !== 3
          ? dayjs.utc(end_date).format('YYYY-MM-DD')
          : '-'
      },
    })

    cols.push({
      title: 'Penalty',
      key: 'offense',
      dataIndex: 'offense',
      render: (offense) =>
        offense?.offense_type?.name_full || offense?.offense_type?.name || '-',
    })

    cols.push({
      title: 'Description',
      key: 'description',
      dataIndex: 'description',
      render: (desc) => desc || '-',
    })

    if (!isProfileView && hasPermission) {
      cols.push({
        title: 'Edit',
        key: 'edit',
        width: 60,
        align: 'center',
        render: (_, record) => (
          <AddSuspensionSheet
            player={record.player}
            team={record.team}
            description={record.description}
            starts_at={record.start_date}
            ends_at={record.end_date}
            isIndefinite={record.enforcement?.id === 3 || !record.end_date}
            comp={record.schedule}
            timezone={record.timezone}
            game={record.game}
            offense={record.offense}
            id={record.id}
            onComplete={refresh}
            button={(toggleOpen) => (
              <EditOutlined
                onClick={toggleOpen}
                css={css`
                  cursor: pointer;
                  font-size: 16px;
                  color: ${colors.PRIMARY};
                  &:hover {
                    color: ${colors.PRIMARY_DARK};
                  }
                `}
              />
            )}
          />
        ),
      })
    }

    return cols
  }, [isProfileView, onlyDisplayActive, hasPermission, scheduleId, refresh])

  if (loading) {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          min-height: 98px;
          margin-top: 24px;
          padding: 20px 16px;
          justify-content: center;
          align-items: center;
          background-color: #27303e;
          border: 1px solid #3f4753;
          border-radius: 8px;

          ${media.mobile} {
            padding: 16px 0;
          }
        `}
      >
        <Spin />
      </div>
    )
  }

  return (
    <>
      <div
        css={css`
          margin: 16px auto;
          display: flex;
          flex-wrap: wrap;
          align-items: flex-end;
          gap: 16px;
          justify-content: space-between;
        `}
      >
        <div
          css={css`
            display: flex;
            gap: 16px;
            flex-wrap: wrap;
            align-items: flex-end;
          `}
        >
          {onlyDisplayActive && (
            <Text
              variant='captionLarge'
              color={colors.PRIMARY}
              weight='semiBold'
            >
              Active Suspensions
            </Text>
          )}

          {hasPermission && (
            <>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 8px;
                `}
              >
                <SelectInput
                  label='Status'
                  value={activeFilter || 'all'}
                  onChange={(value) => {
                    console.log(value)
                    setActiveFilter(value)
                  }}
                  options={[
                    { label: 'All', value: 'all' },
                    { label: 'Active', value: 'active' },
                    { label: 'Completed', value: 'completed' },
                  ]}
                  name='status'
                  fieldErrors={[]}
                  allowClear={true}
                  style={css`
                    width: 200px;
                  `}
                />
              </div>

              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 8px;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.WHITE}
                  style={`
                  align-self: stretch;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                `}
                >
                  Search Player
                </Text>
                <Input
                  prefix={
                    <SearchOutlined
                      css={css`
                        font-size: 16px;
                        color: ${colors.NEUTRAL_50};
                      `}
                    />
                  }
                  allowClear={true}
                  placeholder='Enter a player name'
                  value={playerFilter}
                  onChange={(e) => setPlayerFilter(e.target.value)}
                  css={css`
                    width: 300px;
                    background-color: ${colors.SECONDARY_500};
                    border: 1px solid ${colors.SECONDARY_200};
                    border-radius: 8px;
                    color: ${colors.WHITE};
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 20.3px;
                    @media (max-width: 768px) {
                      width: 100%;
                    }
                    .ant-input {
                      background-color: ${colors.SECONDARY_500};
                      color: ${colors.WHITE};
                    }
                    .ant-input-prefix {
                      color: ${colors.NEUTRAL_100};
                    }
                    &::placeholder {
                      color: ${colors.NEUTRAL_100};
                    }
                  `}
                />
              </div>
              {exportId && (
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-end;
                  `}
                >
                  <Button
                    variant='secondary'
                    onClick={async () => {
                      const response = await req(
                        `/suspensions/schedule/${exportId}/export/csv`,
                        {
                          method: 'GET',
                          parseJSON: false,
                          skipAutoContentType: true,
                          headers: {
                            Accept: 'text/csv',
                          },
                        }
                      )
                      const blob = await response.blob()
                      saveAs(blob, `${exportFileName}.csv`)
                    }}
                    icon={<DownloadOutlined />}
                    text='Export CSV'
                    style={css`
                      max-width: 300px;
                    `}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div
        css={css`
          overflow: auto;
        `}
      >
        <Table
          columns={columns}
          dataSource={filteredSuspensions}
          rowKey='id'
          pagination={false}
          size='small'
          bordered
          scroll={{ x: 1000 }}
          locale={{
            emptyText: isProfileView
              ? 'You Have No Suspensions.'
              : 'There Are No Suspensions For This Competition.',
          }}
          css={css`
            margin-bottom: 16px;
            .ant-table-cell {
              color: ${colors.NEUTRAL_0} !important;
            }
            .ant-table-measure-row {
              display: none !important;
            }
            .ant-table-thead > tr > th {
              padding: 8px 8px !important;
              background-color: ${colors.SECONDARY_300} !important;
            }
            .ant-table-thead > tr > th.ant-table-column-sort {
              isolation: isolate;
              background-color: transparent !important;
              position: relative;
            }
            .ant-table-thead > tr > th.ant-table-column-sort::after {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: ${colors.SECONDARY_300};
              background-image: linear-gradient(
                ${colors.INFO_500},
                ${colors.INFO_500}
              );
              background-blend-mode: color-dodge;
              z-index: -1;
              pointer-events: none;
            }
            .ant-table-tbody > tr:nth-child(even) {
              background-color: ${colors.SECONDARY_400};
            }
            .ant-table-tbody > tr:nth-child(odd) {
              background-color: ${colors.SECONDARY_300};
            }
            .ant-table-tbody > tr > td {
              padding: 8px 8px !important;
              vertical-align: middle;
            }
            .ant-table-tbody > tr > td.ant-table-column-sort {
              isolation: isolate;
              background-color: transparent !important;
            }
            .ant-table-tbody
              > tr
              > td.ant-table-column-sort:not(.ant-table-cell-fix-left):not(
                .ant-table-cell-fix-right
              ) {
              position: relative;
            }
            .ant-table-tbody > tr > td.ant-table-column-sort::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-image: linear-gradient(
                ${colors.INFO_500},
                ${colors.INFO_500}
              );
              background-blend-mode: color-dodge;
              z-index: -1;
              pointer-events: none;
            }
            .ant-table-tbody
              > tr:nth-child(even)
              > td.ant-table-column-sort::before {
              background-color: ${colors.SECONDARY_400};
            }
            .ant-table-tbody
              > tr:nth-child(odd)
              > td.ant-table-column-sort::before {
              background-color: ${colors.SECONDARY_300};
            }
            /* Override sticky column backgrounds when sorted */
            .ant-table-tbody
              > tr
              > td.ant-table-column-sort.ant-table-cell-fix-left {
              background-color: transparent !important;
            }
            @media (max-width: 768px) {
              padding: 0 16px !important;
            }
            .ant-table-tbody
              > tr:nth-child(even)
              > td.ant-table-cell.ant-table-cell-fix-left.ant-table-cell-fix-left-last {
              background-color: ${colors.SECONDARY_400} !important;
            }
            .ant-table-tbody
              > tr:nth-child(odd)
              > td.ant-table-cell.ant-table-cell-fix-left.ant-table-cell-fix-left-last {
              background-color: ${colors.SECONDARY_300} !important;
            }
            .ant-table-tbody > tr:nth-child(even) > td:nth-child(1) {
              background-color: ${colors.SECONDARY_400} !important;
            }
            .ant-table-tbody > tr:nth-child(odd) > td:nth-child(1) {
              background-color: ${colors.SECONDARY_300} !important;
            }
          `}
        />
      </div>
    </>
  )
}

export default Suspensions
